import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import parse from 'html-react-parser';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';
import { setCalendarPerformance } from '../redux/reducer';
import API from '../services/API';

// wtRegion constants
const US = 'US';
const EU = 'EU';

const formatDate = dtStr => {
  const dt = new Date(dtStr);
  const dateFormat = window.performanceChart?.wtRegion === US ? US : EU;

  if (dateFormat === US) {
    return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
  }
  return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
};

const getFullYear = value => {
  const date = new Date(value);
  return date.getFullYear();
};

const waitForChartConfig = () =>
  new Promise(resolve => {
    if (window.performanceChart?.isin) {
      resolve(window.performanceChart);
      return;
    }

    let tries = 0;
    const interval = setInterval(() => {
      tries += 1;

      if (window.performanceChart?.isin || tries >= 30) {
        clearInterval(interval);
        resolve(window.performanceChart || {});
      }
    }, 100);
  });

const getChartTitle = () => window.performanceChart?.title || '';

const getChartISIN = async () => {
  const chartConfig = await waitForChartConfig();
  return chartConfig.isin || '';
};

const formatToTwoDecimalPlaces = num => (num.toFixed(4) * 100).toFixed(2);

const getOptions = data => {
  const { idxBmkName } = data.find(d => d.idxBmkName);
  const { productName } = data.find(d => d.productName);
  return {
    grid: { top: 30, right: 8, bottom: 85, left: 64 },
    color: ['#05a9b1', '#0047BB'],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          formatter: params => {
            if (params.axisDimension === 'y') {
              return `${params.value.toFixed(2)}%`;
            }
            return `${formatDate(params.value)}`;
          },
        },
      },
      valueFormatter: value => `${value}%`,
      textStyle: {
        color(value) {
          if (value < 0) {
            return '#dc3545';
          }
          return '#000';
        },
      },
    },
    legend: {
      show: true,
      data: [productName, idxBmkName],
      top: 'bottom',
      textStyle: {
        fontWeight: 'bolder',
      },
    },
    xAxis: [
      {
        type: 'category',
        data: data.map(x => x.dt),
        axisLabel: {
          rotate: 45,
          formatter: getFullYear,
          fontWeight: 'bold',
        },
        boundaryGap: true,
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: i18next.t('PERCENTAGE'),
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyleObject: {
          fontSize: 20,
        },
        axisLabel: {
          formatter(value) {
            return `${value}.0%`;
          },
          textStyle: {
            color(value) {
              if (value < 0) {
                return '#dc3545';
              }
              return '#000';
            },
          },
        },
      },
    ],
    series: [
      {
        name: productName,
        type: 'bar',
        data: data.map(x => x.product),
        symbol: 'circle',
        barMaxWidth: 30,
      },

      {
        name: idxBmkName,
        type: 'bar',
        data: data.map(x => x.idxBmk),
        symbol: 'circle',
        barMaxWidth: 30,
      },
    ],
  };
};

const CalendarPerformanceChart = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    let isActive = true;

    const fetchOptions = async () => {
      try {
        const isin = await getChartISIN();

        if (!isin || !isActive) {
          return;
        }

        const response = await API.getCalendarPerformanceChartData(isin);

        if (!Array.isArray(response) || response.length === 0 || !isActive) {
          return;
        }

        const updatedData = response.map(d => ({
          ...d,
          product: formatToTwoDecimalPlaces(d.product),
          idxBmk: formatToTwoDecimalPlaces(d.idxBmk),
        }));

        setOptions(getOptions(updatedData));
        dispatch(setCalendarPerformance(updatedData));
      } catch {
        setOptions(null);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchOptions();

    return () => {
      isActive = false;
    };
  }, [dispatch]);

  return (
    <div>
      {loading && (
        <div className="loader-overlay">
          <div className="loader" />
        </div>
      )}
      {options && (
        <>
          <h3 className="typ-h3">{parse(getChartTitle())}</h3>
          <ReactECharts
            option={options}
            notMerge
            lazyUpdate
            onChartReady={() => {}}
            onEvents={() => {}}
            style={{
              width: '100%',
            }}
            opts={{ renderer: 'svg' }}
          />
        </>
      )}
    </div>
  );
};

export default CalendarPerformanceChart;
